import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IoArrowBack, IoEyeOutline, IoEyeOffOutline, IoCheckmarkCircle, IoCloseCircle } from 'react-icons/io5';
import { passwordService } from '../../../services/passwordService';
import logo from '../../../assets/Header/magnetix.png';
import './CreateNewPassword.css';

const CreateNewPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Get email and verification code from location state
  const email = (location.state as any)?.email;
  const verificationCode = (location.state as any)?.verificationCode;

  // Password requirements checks
  const requirements = useMemo(() => ({
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  }), [password]);

  const allRequirementsMet = Object.values(requirements).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  // Redirect if no email or verification code provided
  useEffect(() => {
    if (!email || !verificationCode) {
      navigate('/forgot-password', { replace: true });
    }
  }, [email, verificationCode, navigate]);

  const handleSubmit = async () => {
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (!allRequirementsMet) {
      setError('Password does not meet all requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await passwordService.resetPassword(email, verificationCode, password);

      // Navigate to login with success message
      navigate('/login', {
        replace: true,
        state: { message: 'Password reset successfully! Please login with your new password.' }
      });
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
      console.error('Reset password error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="create-password-container">
      <div className="create-password-header">
        <div className="create-password-logo">
          <img src={logo} alt="Magnetix Logo" className="create-password-logo-image" />
        </div>
      </div>

      <div className="create-password-card">

        <h1 className="create-password-title">Create New Password</h1>
        <p className="create-password-subtitle">Set your new & strong password for security.</p>

        <div className="create-password-form">
          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px',
              marginBottom: '16px',
              backgroundColor: '#FEE',
              border: '1px solid #EF4444',
              borderRadius: '8px',
              color: '#B91C1C',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <div className="create-password-form-group">
            <label className="create-password-label" htmlFor="password">
              Enter Password
            </label>
            <div className="create-password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="create-password-input"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                placeholder="Enter your new password"
                disabled={isLoading}
              />
              <button
                type="button"
                className="create-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label="Toggle password visibility"
                disabled={isLoading}
              >
                {showPassword ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
              </button>
            </div>
          </div>

          {/* Password Requirements Checklist */}
          {password.length > 0 && (
            <div className="create-password-requirements">
              {[
                { key: 'minLength', label: 'At least 8 characters' },
                { key: 'hasUppercase', label: 'One uppercase letter (A-Z)' },
                { key: 'hasLowercase', label: 'One lowercase letter (a-z)' },
                { key: 'hasNumber', label: 'One number (0-9)' },
                { key: 'hasSpecial', label: 'One special character (!@#$%^&*...)' },
              ].map(({ key, label }) => (
                <div key={key} className={`create-password-req-item ${requirements[key as keyof typeof requirements] ? 'met' : 'unmet'}`}>
                  {requirements[key as keyof typeof requirements]
                    ? <IoCheckmarkCircle size={16} />
                    : <IoCloseCircle size={16} />
                  }
                  <span>{label}</span>
                </div>
              ))}
            </div>
          )}

          <div className="create-password-form-group">
            <label className="create-password-label" htmlFor="confirmPassword">
              Re-enter Password
            </label>
            <div className="create-password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                className="create-password-input"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                }}
                placeholder="Confirm your new password"
                disabled={isLoading}
              />
              <button
                type="button"
                className="create-password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label="Toggle password visibility"
                disabled={isLoading}
              >
                {showConfirmPassword ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            className="create-password-submit-button"
            disabled={isLoading || !allRequirementsMet || !passwordsMatch}
          >
            {isLoading ? 'Creating Password...' : 'Create Password'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateNewPassword;
